'use client';

import clsx from 'clsx/lite';
import { AnimatePresence, motion } from 'motion/react';
import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';
import { useQueryState } from 'nuqs';
import { useEffect, useMemo, useRef, useState } from 'react';
import slugify from 'react-slugify';
import Logo from '~/assets/img/ico-ausl.svg';
import { Button } from '~/components/Buttons';
import { CustomDropdown } from '~/components/CustomDropdown';
import { useDebounce } from '~/hooks/useDebounce';
import { useSearch } from '~/hooks/useSearch';
import { IGame, IPlayer } from '~/types/api';
import { getColors } from '~/utils/getColors';
import { getLocalDateTime } from '~/utils/getLocalDateTime';
import { getLogo } from '~/utils/getLogo';

export interface IPage {
  uri: string;
  title: string;
  isTeamPage?: boolean;
}

export interface INews {
  title: string;
  uri: string;
  slug: string;
  featuredImage: {
    node: {
      sourceUrl: string;
      altText: string;
    };
  };
  categories: {
    nodes: {
      name: string;
    }[];
  };
}

const closeMenu = new Event('menu:close');

export function Search() {
  const pathname = usePathname();
  const router = useRouter();

  const [isOpen, setIsOpen] = useState(false);
  const [searchInputValue, setSearchInputValue] = useState<string>('');
  const [searchTerm, setSearchTerm] = useQueryState('q');
  const [contentFilter, setContentFilter] = useQueryState('type', {
    defaultValue: '',
  });
  const [hasResults, setHasResults] = useState(false);

  // Debounce the search input to prevent excessive API calls
  const debouncedSearchTerm = useDebounce(searchInputValue, 300);

  // Update the URL search parameter when the debounced term changes
  useEffect(() => {
    if (debouncedSearchTerm) {
      setSearchTerm(debouncedSearchTerm);
    } else if (debouncedSearchTerm === '') {
      setSearchTerm(null);
    }
  }, [debouncedSearchTerm, setSearchTerm]);

  // Initialize search input from URL parameter
  useEffect(() => {
    if (searchTerm) {
      setSearchInputValue(searchTerm);
    }
  }, []);

  // Use the React Query hook for search
  const { data: searchResults, isLoading } = useSearch(
    searchTerm,
    contentFilter || undefined,
  );

  // Create memoized references to search results for better performance
  const players = useMemo(
    () => searchResults?.players || [],
    [searchResults?.players],
  );
  const games = useMemo(
    () => searchResults?.games || [],
    [searchResults?.games],
  );
  const posts = useMemo(() => searchResults?.news || [], [searchResults?.news]);
  const pages = useMemo(
    () => searchResults?.pages || [],
    [searchResults?.pages],
  );

  // Update the hasResults state based on search results
  useEffect(() => {
    if (searchResults) {
      const hasAnyResults =
        players.length > 0 ||
        games.length > 0 ||
        posts.length > 0 ||
        pages.length > 0;

      setHasResults(hasAnyResults);
    } else {
      setHasResults(false);
    }
  }, [searchResults, players, games, posts, pages]);

  useEffect(() => {
    setIsOpen(false);
  }, [pathname]);

  const closeSearch = () => {
    setIsOpen(false);
  };

  useEffect(() => {
    window.addEventListener('search:close', closeSearch);
    return () => {
      window.removeEventListener('search:close', closeSearch);
    };
  }, []);

  useEffect(() => {
    setIsOpen(false);
  }, [router]);

  const handleClick = () => {
    window.dispatchEvent(closeMenu);
    setIsOpen(!isOpen);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchInputValue(e.target.value);
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    // Set the search term immediately on form submission
    setSearchTerm(searchInputValue || null);
  };

  // Use the filtered results directly from query
  const futureGames = useMemo(() => games, [games]);
  const filteredPlayers = useMemo(() => players, [players]);
  const filteredNews = useMemo(() => posts, [posts]);
  const filteredPages = useMemo(() => pages, [pages]);

  const total =
    futureGames.length +
    filteredPlayers.length +
    filteredNews.length +
    filteredPages.length;

  const topResults = [
    ...(contentFilter === 'players' || contentFilter === ''
      ? filteredPlayers
      : []),
    ...(contentFilter === 'news' || contentFilter === '' ? filteredNews : []),
    ...(contentFilter === 'pages' || contentFilter === '' ? filteredPages : []),
  ];

  return (
    <>
      <button
        className="absolute right-5 top-[35px] flex w-5 items-center justify-center rounded lg:right-10 lg:top-6 lg:h-12 lg:w-16 lg:bg-[#091724]"
        onClick={handleClick}
        aria-label="Toggle search"
        aria-controls="search-pane"
        aria-expanded={isOpen}
      >
        {isOpen ? (
          <svg fill="none" viewBox="0 0 20 20" className="w-full lg:w-4">
            <path
              stroke="#fff"
              strokeWidth="2"
              strokeLinecap="round"
              d="m1 1 9 9m9 9-9-9m0 0 9-9m-9 9-9 9"
            />
          </svg>
        ) : (
          <svg viewBox="0 0 21 22" className="w-full lg:w-4">
            <path
              fill="#fff"
              d="M2.986 9.057a6.004 6.004 0 1 1 12.01 0 6.004 6.004 0 0 1-12.01 0ZM8.991.934a8.124 8.124 0 1 0 4.657 14.78l4.939 4.94a1.413 1.413 0 1 0 1.998-1.998l-4.939-4.939A8.124 8.124 0 0 0 8.991.933Z"
            />
          </svg>
        )}
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            key="search-backdrop"
            className="fixed inset-0 bg-black/50"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => {
              setIsOpen(false);
            }}
          />
        )}

        {isOpen && (
          <motion.div
            key="search-pane"
            id="search-pane"
            initial={{ height: 0 }}
            animate={{ height: 'auto' }}
            exit={{ height: 0 }}
            className={clsx(
              'absolute inset-x-0 top-full z-100 w-auto lg:fixed lg:top-0',
              hasResults && 'overflow-hidden',
            )}
            aria-label="header menu"
          >
            <div className="flex bg-white px-[--site-margin] text-black">
              <div className="mx-auto my-12 flex w-full max-w-[--content-width] flex-col gap-y-12 max-lg:h-[calc(100vh-126px)] lg:border lg:p-9 [&>*]:mx-auto [&>*]:w-full [&>*]:max-w-[864px]">
                <div className="flex flex-col gap-y-[18px]">
                  <form
                    className="flex gap-x-2.5 lg:gap-x-3"
                    onSubmit={handleSubmit}
                  >
                    <input
                      type="text"
                      className="h-9 w-full border-b border-current pb-[11px] pt-[5px] text-[28px] font-black uppercase outline-none placeholder:text-[#7A7A7A] lg:h-12 lg:pb-[13px] lg:pt-2.5 lg:text-[36px]"
                      value={searchInputValue}
                      onChange={handleChange}
                      placeholder="Search"
                    />
                    <button
                      type="submit"
                      className="order-last flex h-9 w-9 items-center justify-center rounded bg-black lg:h-12 lg:min-w-12"
                      disabled={isLoading}
                    >
                      {isLoading ? (
                        <motion.div
                          className="h-4 w-4 rounded-full border-2 border-white border-t-transparent"
                          animate={{ rotate: 360 }}
                          transition={{
                            duration: 1,
                            repeat: Infinity,
                            ease: 'linear',
                          }}
                        />
                      ) : (
                        <svg
                          width="26"
                          height="12"
                          fill="none"
                          stroke="#fff"
                          strokeWidth="1.2"
                        >
                          <path d="M5.8 10.803a4.8 4.8 0 1 0 0-9.6 4.8 4.8 0 0 0 0 9.6Z" />
                          <path d="M5.8 6.004H25m0 0-4.168-4m4.169 4-4.17 4" />
                        </svg>
                      )}
                      <span className="sr-only">Search</span>
                    </button>
                    <CustomDropdown
                      options={[
                        { value: 'players', label: 'Athletes' },
                        { value: 'games', label: 'Games' },
                        { value: 'news', label: 'News' },
                        { value: 'pages', label: 'Pages' },
                      ]}
                      label="All"
                      value={contentFilter || ''}
                      onChange={(value) => {
                        setContentFilter(
                          (value as 'players' | 'games' | 'news' | 'pages') ||
                            null,
                        );
                      }}
                      className="max-lg:hidden lg:min-w-[165px]"
                    />
                  </form>

                  {hasResults && (
                    <div>
                      <motion.p
                        className="text-[12px] leading-[9px] text-[#7A7A7A]"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.2 }}
                      >
                        {total} results for "
                        <span className="font-medium text-black">
                          {searchInputValue}
                        </span>
                        "
                      </motion.p>
                    </div>
                  )}
                </div>

                {hasResults && (
                  <>
                    <div className="gapy-y-9 flex grid-cols-2 flex-col lg:grid">
                      {(contentFilter === '' || contentFilter === 'games') && (
                        <div className="flex flex-col gap-y-9 font-nova">
                          <h2 className="text-[12px] font-bold uppercase leading-[9px]">
                            Upcoming Games & Events
                          </h2>

                          <ul className="flex flex-col gap-y-6">
                            {futureGames.length > 0 ? (
                              futureGames
                                .slice(0, 5)
                                .map((game, index) => (
                                  <GameEvent key={index} game={game} />
                                ))
                            ) : (
                              <li className="text-gray-500 text-sm">
                                No upcoming games or events found
                              </li>
                            )}
                          </ul>
                        </div>
                      )}

                      {contentFilter !== 'games' && (
                        <div className="flex flex-col gap-y-9 font-nova">
                          <h2 className="text-[12px] font-bold uppercase leading-[9px]">
                            Top Results
                          </h2>

                          {topResults.length > 0 ? (
                            topResults.slice(0, 4).map((item, index) => {
                              if ('firstName' in item) {
                                return <Profile key={index} player={item} />;
                              }

                              if ('categories' in item) {
                                return (
                                  <News key={index} news={item as INews} />
                                );
                              }

                              return <Page key={index} page={item as IPage} />;
                            })
                          ) : (
                            <div className="text-gray-500 text-sm">
                              No results found
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    <div className="flex justify-center">
                      <Button
                        href={
                          contentFilter
                            ? `/search?q=${encodeURIComponent(searchInputValue || '')}&type=${contentFilter}`
                            : `/search?q=${encodeURIComponent(searchInputValue || '')}`
                        }
                        isSmall
                        hideDesktopIcon
                        hideMobileIcon
                        className="!max-w-max"
                      >
                        See All
                      </Button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

export function GameEvent({ game }: { game: IGame }) {
  const ref = useRef<HTMLAnchorElement>(null);

  if (!game) return null;

  const localDateTime = game.gameDateIso
    ? getLocalDateTime(game.gameDateIso)
    : null;

  return (
    <li
      className="flex cursor-pointer flex-col gap-y-3"
      onClick={() => {
        if (ref.current) {
          ref.current.click();
        }
      }}
    >
      <span
        className="text-[8px] font-bold uppercase leading-[5px]"
        suppressHydrationWarning={true}
      >
        {localDateTime?.date} {localDateTime?.time}
      </span>
      <div>
        <h3 className="-mb-[7px] -mt-1.5 text-[16px] font-semibold tracking-[-0.019em]">
          <Link ref={ref} href={`/game/${game.gameId}`}>
            {game.competitors?.[0]?.name} vs. {game.competitors?.[1]?.name}
          </Link>
        </h3>
      </div>
      <span className="text-[10px] font-semibold leading-[7px]">
        {game.venue?.name}
      </span>
    </li>
  );
}

export function Profile({ player }: { player: IPlayer }) {
  const ref = useRef<HTMLAnchorElement>(null);

  if (!player) return null;

  // console.log('player', player);

  const url = player?.franchise?.teamName
    ? `/${player.franchise.teamName.toLowerCase()}/roster/${slugify(player.firstName + ' ' + player.lastName)}`
    : `/players/${slugify(player.firstName + ' ' + player.lastName)}`;

  return (
    <li
      className="grid cursor-pointer grid-cols-[auto_1fr] grid-rows-[auto_1fr] gap-x-3 gap-y-3"
      onClick={() => {
        if (ref.current) {
          ref.current.click();
        }
      }}
    >
      <div className="row-span-2 h-12 w-12 overflow-hidden rounded-full bg-black">
        {player.imageResource?.imageUrl && (
          <img
            className="h-full w-full"
            src={player.imageResource.imageUrl}
            alt={player.imageResource?.altText}
          />
        )}
      </div>
      <span className="text-[8px] font-bold uppercase leading-[5px]">
        Profile
      </span>
      <h3 className="-mb-[7px] -mt-1.5 text-[16px] font-semibold tracking-[-0.019em]">
        <Link ref={ref} href={url}>
          {player.firstName} {player.lastName}
        </Link>
      </h3>
    </li>
  );
}

export function News({ news }: { news: INews }) {
  const ref = useRef<HTMLAnchorElement>(null);

  if (!news) return null;

  const category =
    news.categories.nodes.find((node) => node.name !== 'News')?.name || 'News';

  return (
    <li
      className="grid cursor-pointer grid-cols-[auto_1fr] grid-rows-[auto_1fr] gap-x-3 gap-y-3"
      onClick={() => {
        if (ref.current) {
          ref.current.click();
        }
      }}
    >
      <div className="row-span-2 h-12 w-12 overflow-hidden rounded bg-navy">
        {news.featuredImage?.node?.sourceUrl ? (
          <img
            className="h-full w-full object-cover"
            src={news.featuredImage?.node?.sourceUrl}
            alt={news.featuredImage?.node?.altText}
          />
        ) : (
          <img className="h-full w-full" src={Logo.src} alt="" />
        )}
      </div>
      <span className="text-[8px] font-bold uppercase leading-[5px]">
        {category}
      </span>
      <h3 className="-mb-[7px] -mt-1.5 text-[16px] font-semibold tracking-[-0.019em]">
        <Link ref={ref} href={news.uri || `/news/${news.slug}`}>
          {news.title}
        </Link>
      </h3>
    </li>
  );
}

export function Page({ page }: { page: IPage }) {
  const ref = useRef<HTMLAnchorElement>(null);

  if (!page) return null;

  return (
    <li
      className="grid cursor-pointer grid-cols-[auto_1fr] grid-rows-[auto_1fr] gap-x-3 gap-y-3"
      onClick={() => {
        if (ref.current) {
          ref.current.click();
        }
      }}
    >
      <div
        className="row-span-2 h-12 w-12 overflow-hidden rounded p-2"
        style={{ backgroundColor: getColors(page.title.toLowerCase())?.bg }}
      >
        {page.isTeamPage ? (
          <img
            className="h-full w-full"
            src={getLogo(page.title.toLowerCase())?.icon.src}
            alt=""
          />
        ) : (
          <img className="h-full w-full" src={Logo.src} alt="" />
        )}
      </div>
      <span className="text-[8px] font-bold uppercase leading-[5px]">
        {page.isTeamPage ? 'Team Homepage' : 'Page'}
      </span>
      <h3 className="-mb-[7px] -mt-1.5 text-[16px] font-semibold tracking-[-0.019em]">
        <Link ref={ref} href={page.uri}>
          {page.title}
        </Link>
      </h3>
    </li>
  );
}

// export function Video() {
//   return (
//     <li className="grid grid-cols-[auto_1fr] grid-rows-[auto_1fr] gap-x-3 gap-y-3">
//       <div className="relative row-span-2 h-12 w-12 overflow-hidden rounded bg-navy">
//         <img
//           src="/allstarcup-schedule-a.png"
//           className="h-full w-full"
//           alt=""
//         />
//         <svg
//           width="48"
//           height="48"
//           fill="none"
//           viewBox="0 0 48 48"
//           className="absolute inset-0 h-12 w-12"
//         >
//           <g filter="url(#a)">
//             <path d="M39 24 16.5 36.99V11.01L39 24Z" fill="#fff" />
//           </g>
//           <defs>
//             <filter
//               id="a"
//               x="-11.6"
//               y="-13.09"
//               width="78.7"
//               height="82.18"
//               filterUnits="userSpaceOnUse"
//               colorInterpolationFilters="sRGB"
//             >
//               <feFlood floodOpacity="0" result="BackgroundImageFix" />
//               <feColorMatrix
//                 in="SourceAlpha"
//                 values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
//                 result="hardAlpha"
//               />
//               <feOffset dy="4" />
//               <feGaussianBlur stdDeviation="14.05" />
//               <feComposite in2="hardAlpha" operator="out" />
//               <feColorMatrix values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.25 0" />
//               <feBlend
//                 in2="BackgroundImageFix"
//                 result="effect1_dropShadow_2910_26329"
//               />
//               <feBlend
//                 in="SourceGraphic"
//                 in2="effect1_dropShadow_2910_26329"
//                 result="shape"
//               />
//             </filter>
//           </defs>
//         </svg>
//       </div>
//       <span className="text-[8px] font-bold uppercase leading-[5px]">
//         Nature
//       </span>
//       <h3 className="-mb-[7px] -mt-1.5 text-[16px] font-semibold tracking-[-0.019em]">
//         Steady Performance by Hayward Helps Empower to Secure Win
//       </h3>
//     </li>
//   );
// }
